import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import ProductCard from '@/components/products/ProductCard';
import BlogPostCard from '@/components/blog/BlogPostCard';
import { useQuery } from '@tanstack/react-query';
import { Skeleton } from '@/components/ui/skeleton';
import axios from 'axios';

const fetchFeaturedProducts = async () => {
  try {
    const response = await axios.get('/api/products');
    // Ensure we always return an array
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    console.error('Error fetching products:', error);
    return []; // Return empty array on error
  }
};

const fetchLatestBlogPosts = async () => {
  try {
    const response = await axios.get('/api/blog-posts');
    // Ensure we always return an array
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    console.error('Error fetching blog posts:', error);
    return []; // Return empty array on error
  }
};

const Index = () => {
  const { data: featuredProducts = [], isLoading: isLoadingProducts, error: productsError } = useQuery({
    queryKey: ['featuredProducts'],
    queryFn: fetchFeaturedProducts,
  });

  const { data: latestBlogPosts = [], isLoading: isLoadingBlogs } = useQuery({
    queryKey: ['latestBlogPosts'],
    queryFn: fetchLatestBlogPosts,
  });

  // Add console logs for debugging
  
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <main className="flex-grow">
        <section className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-gray-900 dark:to-gray-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
            <div className="text-center">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 dark:text-white">
                NISOO
              </h1>
              <p className="mt-4 text-lg md:text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
                Explore the best products to help you ship your next application faster and better
              </p>
            </div>
          </div>
        </section>
        
        <section className="py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center mb-8">
              <h2 className="text-2xl font-bold">Featured Products</h2>
              <Link to="/products" className="text-primary font-medium flex items-center hover:underline">
                View all <ArrowRight size={16} className="ml-1" />
              </Link>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {isLoadingProducts ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="space-y-4">
                    <Skeleton className="h-40 w-full" />
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-4 w-1/2" />
                  </div>
                ))
              ) : productsError ? (
                <div className="col-span-4 text-center py-8 text-red-500">
                  Failed to load products. Please try again later.
                </div>
              ) : !featuredProducts || featuredProducts.length === 0 ? (
                <div className="col-span-4 text-center py-8 text-gray-500">
                  No featured products available
                </div>
              ) : (
                // Ensure featuredProducts is an array before mapping and limit to 4 items for one row
                // Prioritize featured products, then fill with latest if needed
                (() => {
                  if (!Array.isArray(featuredProducts)) return [];
                  const featured = featuredProducts.filter(p => p.is_featured).slice(0, 4);
                  if (featured.length >= 4) return featured;
                  const latest = featuredProducts.filter(p => !p.is_featured).slice(0, 4 - featured.length);
                  return [...featured, ...latest];
                })().map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))
              )}
            </div>
          </div>
        </section>

        <section className="py-12 bg-gray-50 dark:bg-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center mb-8">
              <h2 className="text-2xl font-bold">Latest from the Blog</h2>
              <Link to="/blog" className="text-primary font-medium flex items-center hover:underline">
                View all <ArrowRight size={16} className="ml-1" />
              </Link>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {isLoadingBlogs ? (
                <div className="col-span-3 text-center py-8 text-gray-500">Loading blog posts...</div>
              ) : !latestBlogPosts || latestBlogPosts.length === 0 ? (
                <div className="col-span-3 text-center py-8 text-gray-500">No blog posts available</div>
              ) : (
                // Ensure latestBlogPosts is an array before mapping and limit to 3 items for one row
                // Prioritize featured posts, then fill with latest if needed
                (() => {
                  if (!Array.isArray(latestBlogPosts)) return [];
                  const featured = latestBlogPosts.filter(p => p.is_featured).slice(0, 3);
                  if (featured.length >= 3) return featured;
                  const latest = latestBlogPosts.filter(p => !p.is_featured).slice(0, 3 - featured.length);
                  return [...featured, ...latest];
                })().map((post) => (
                  <BlogPostCard
                    key={post.id} 
                    post={{
                      id: post.id,
                      title: post.title,
                      excerpt: post.excerpt || '',
                      author: post.author,
                      date: new Date(post.created_at).toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      }),
                      readTime: post.read_time || '3 min read',
                      slug: post.slug
                        ? post.slug
                        : post.title.trim().replace(/\s+/g, '-'),
                      content: post.content,
                      image: post.image,
                      tags: [],
                      groupId: post.group_id
                    }} 
                  />
                ))
              )}
            </div>
          </div>
        </section>

      </main>
      
      <Footer />
    </div>
  );
};

export default Index;
